import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CompareProvider } from "@/context/CompareContext";
import { Loader } from "@/components/common";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import PublicLayout from "@/layouts/PublicLayout";

const Home = lazy(() => import("@/pages/Home"));
const SearchResults = lazy(() => import("@/pages/SearchResults"));
const CollegeDetail = lazy(() => import("@/pages/CollegeDetail"));
const CoursesList = lazy(() => import("@/pages/CoursesList"));
const CoursePage = lazy(() => import("@/pages/CoursePage"));
const ExamsList = lazy(() => import("@/pages/ExamsList"));
const ExamPage = lazy(() => import("@/pages/ExamPage"));
const Rankings = lazy(() => import("@/pages/Rankings"));
const ArticlesList = lazy(() => import("@/pages/ArticlesList"));
const ArticleDetail = lazy(() => import("@/pages/ArticleDetail"));
const Compare = lazy(() => import("@/pages/Compare"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const StudentLogin = lazy(() => import("@/pages/StudentLogin"));
const StudentDashboard = lazy(() => import("@/pages/StudentDashboard"));
const StaticPage = lazy(() => import("@/pages/StaticPage"));
const DegreeLanding = lazy(() => import("@/pages/DegreeLanding"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const SavedColleges = lazy(() => import("@/pages/SavedColleges"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminColleges = lazy(() => import("@/pages/admin/AdminColleges"));
const CollegeForm = lazy(() => import("@/pages/admin/CollegeForm"));
const AdminCrud = lazy(() => import("@/pages/admin/AdminCrud"));
const AdminArticles = lazy(() => import("@/pages/admin/AdminArticles"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminRankings = lazy(() => import("@/pages/admin/AdminRankings"));
const AdminBulkImport = lazy(() => import("@/pages/admin/AdminBulkImport"));
const AdminDataQuality = lazy(() => import("@/pages/admin/AdminDataQuality"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AdminAuditLogs"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminHistory = lazy(() => import("@/pages/admin/AdminHistory"));
const CatchAll = lazy(() => import("@/pages/CatchAll"));

function App() {
  useEffect(() => { sessionStorage.removeItem("lmc_chunk_reload"); }, []);
  return (
    <ErrorBoundary>
    <AuthProvider>
      <CompareProvider>
        <BrowserRouter>
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/mba" element={<DegreeLanding degree="MBA" />} />
                <Route path="/pgdm" element={<DegreeLanding degree="PGDM" />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/colleges" element={<SearchResults />} />
                <Route path="/saved-colleges" element={<SavedColleges />} />
                <Route path="/college/:slug" element={<CollegeDetail />} />
                <Route path="/courses" element={<CoursesList />} />
                <Route path="/courses/:slug" element={<CoursePage />} />
                <Route path="/exams" element={<ExamsList />} />
                <Route path="/exams/:slug" element={<ExamPage />} />
                <Route path="/rankings" element={<Rankings />} />
                <Route path="/rankings/:category" element={<Rankings />} />
                <Route path="/news" element={<ArticlesList />} />
                <Route path="/articles" element={<ArticlesList />} />
                <Route path="/articles/:slug" element={<ArticleDetail />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/login" element={<StudentLogin />} />
                <Route path="/dashboard" element={<StudentDashboard />} />
                <Route path="/page/:slug" element={<StaticPage />} />
                <Route path="*" element={<CatchAll />} />
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="colleges" element={<AdminColleges />} />
                <Route path="colleges/new" element={<CollegeForm />} />
                <Route path="colleges/:slug" element={<CollegeForm />} />
                <Route path="colleges/:slug/history" element={<AdminHistory />} />
                <Route path="courses" element={<AdminCrud resource="courses" />} />
                <Route path="exams" element={<AdminCrud resource="exams" />} />
                <Route path="cities" element={<AdminCrud resource="cities" />} />
                <Route path="articles" element={<AdminArticles />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="rankings" element={<AdminRankings />} />
                <Route path="bulk-import" element={<AdminBulkImport />} />
                <Route path="data-quality" element={<AdminDataQuality />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="*" element={<AdminDashboard />} />
              </Route>
            </Routes>
          </Suspense>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </CompareProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
